import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import {Tag} from "primereact/tag";
import {usePayment} from "../../models/payment/use-payment";
import {setWorkspace} from "../../system/app-slice";
import {useTranslate} from "../../system/useTranslate";
import {
    markWorkspaceOnboardingCheckoutRetry,
    markWorkspaceOnboardingPaymentCompleted,
} from "../workspace/onboarding-session";

function PaymentCallback() {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const {getZarinpalPaymentStatus} = usePayment();
    const query = new URLSearchParams(location.search);
    const authority = query.get("authority") || "";
    const callbackResult = query.get("result") || "failed";
    const callbackReference = query.get("reference") || "";
    const callbackWorkspaceDocumentId = query.get("workspaceDocumentId") || "";
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [attempt, setAttempt] = useState(0);
    const [resumeOnboarding, setResumeOnboarding] = useState(false);

    useEffect(() => {
        let mounted = true;
        if (!authority) {
            setLoading(false);
            setError("payment.callback.missing-authority");
            return undefined;
        }

        setLoading(true);
        setError(null);
        getZarinpalPaymentStatus(authority, callbackWorkspaceDocumentId)
            .then((data) => {
                if (!mounted) return;
                setResult(data);
                if (data?.workspace?.documentId) dispatch(setWorkspace(data.workspace.documentId));
                if (data?.state === "success") {
                    setResumeOnboarding(Boolean(markWorkspaceOnboardingPaymentCompleted(data?.workspace?.documentId)));
                }
                if (data?.state === "failed") markWorkspaceOnboardingCheckoutRetry(data?.workspace?.documentId);
            })
            .catch(() => {
                if (mounted) setError("payment.callback.status-unavailable");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, [attempt, authority, callbackWorkspaceDocumentId, dispatch, getZarinpalPaymentStatus]);

    const state = error ? "failed" : result?.state || callbackResult;
    const success = state === "success";
    const pending = state === "pending";
    const severity = success ? "success" : pending ? "warn" : "error";
    const icon = success ? "pi pi-check-circle" : pending ? "pi pi-clock" : "pi pi-times-circle";
    const reference = result?.reference || callbackReference || "-";

    return (
        <div dir="rtl" className="w-full h-full flex align-items-center justify-content-center p-3 text-xs">
            <Card
                className="w-full md:w-8 lg:w-6 xl:w-5 border-1 border-200 shadow-none"
                pt={{body: {className: "p-4"}, content: {className: "p-0"}}}
            >
                {loading ? (
                    <div className="flex flex-column align-items-center gap-3 py-5">
                        <ProgressSpinner className="w-3rem h-3rem" strokeWidth="4"/>
                        <span className="text-xs text-600">{translate("payment.callback.checking")}</span>
                    </div>
                ) : (
                    <div className="flex flex-column gap-3">
                        <div className="flex align-items-center gap-2">
                            <i className={`${icon} text-2xl`}/>
                            <div className="flex flex-column gap-1">
                                <span className="text-base font-bold">{translate(`payment.callback.${state}.title`)}</span>
                                <span className="text-xs text-600">{translate(`payment.callback.${state}.description`)}</span>
                            </div>
                        </div>

                        <Message
                            className="w-full text-xs"
                            severity={severity}
                            text={translate(error || `payment.callback.${state}.message`)}
                            pt={{text: {className: "text-xs"}}}
                        />

                        <div className="surface-50 border-1 border-200 border-round-lg p-3 flex flex-column gap-2">
                            <div className="flex align-items-center justify-content-between gap-2">
                                <span className="text-xs text-600">{translate("payment.callback.status")}</span>
                                <Tag className="text-xs" severity={severity} value={translate(`payment.callback.${state}.status`)}/>
                            </div>
                            <div className="flex align-items-center justify-content-between gap-2">
                                <span className="text-xs text-600">{translate("payment.callback.order-number")}</span>
                                <span className="text-xs font-bold ltr">{result?.order?.orderNumber || "-"}</span>
                            </div>
                            <div className="flex align-items-center justify-content-between gap-2">
                                <span className="text-xs text-600">{translate("payment.callback.reference")}</span>
                                <span className="text-xs font-bold ltr">{reference}</span>
                            </div>
                        </div>

                        <div className="flex flex-column sm:flex-row-reverse gap-2">
                            <Button
                                className="text-xs gap-2"
                                icon="pi pi-cog"
                                label={translate(resumeOnboarding ? "payment.callback.continue-onboarding" : "payment.callback.return-settings")}
                                onClick={() => navigate(resumeOnboarding ? "/workspace/create" : "/settings")}
                            />
                            {(pending || error) && (
                                <Button
                                    className="text-xs gap-2"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    label={translate("payment.callback.retry-status")}
                                    onClick={() => setAttempt((value) => value + 1)}
                                />
                            )}
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}

export default PaymentCallback;
